import {
  getDefaultView,
  getAllDefaultViews,
  setDefaultViewForUser,
  removeDefaultViewForUser
} from '@/services/default-views-api.service'
import * as Sentry from '@sentry/browser'
import { logger } from '@/utils/logger'

const Mutations = {
  SET_DEFAULT_VIEW: 'SET_DEFAULT_VIEW',
  CLEAR_DEFAULT_VIEW: 'CLEAR_DEFAULT_VIEW',
  SET_LOADING: 'SET_LOADING'
}

/**
 * Generate default state for the module
 */
const getDefaultState = () => {
  return {
    // Store default views by boardId
    defaultViews: {},
    isLoading: false
  }
}

const state = getDefaultState()

const getters = {
  /**
   * Check if default views are currently loading
   * @param {Object} _state - The current state
   * @returns {Boolean} - Whether views are loading
   */
  isLoading: (_state) => {
    return _state.isLoading
  },

  /**
   * Get the default view for a specific board
   * @param {Object} _state - The current state
   * @returns {Function} - Function that takes a boardId and returns the default view ID
   */
  getDefaultView: (_state) => (boardId) => {
    return _state.defaultViews[boardId] || null
  }
}

const mutations = {
  [Mutations.SET_DEFAULT_VIEW](_state, { boardId, viewId }) {
    _state.defaultViews = {
      ..._state.defaultViews,
      [boardId]: viewId
    }
  },

  [Mutations.CLEAR_DEFAULT_VIEW](_state, boardId) {
    const newDefaultViews = { ..._state.defaultViews }
    delete newDefaultViews[boardId]
    _state.defaultViews = newDefaultViews
  },

  [Mutations.SET_LOADING](_state, isLoading) {
    _state.isLoading = isLoading
  },

  RESET_MODULE(_state) {
    // Merge rather than replace so we don't lose observers
    Object.assign(_state, getDefaultState())
  }
}

const actions = {
  /**
   * Fetch the default view for a specific board
   * @param {Object} context - Vuex context
   * @param {String} boardId - The board ID to fetch the default view for
   * @returns {Promise} - The default view data
   */
  async fetchDefaultView({ commit }, boardId) {
    if (!boardId) return null

    commit(Mutations.SET_LOADING, true)

    try {
      const response = await getDefaultView({ boardId })

      if (response && response.viewId) {
        commit(Mutations.SET_DEFAULT_VIEW, {
          boardId,
          viewId: response.viewId
        })
      }

      return response
    } catch (error) {
      logger.error('Error fetching default view:', error.message)
      Sentry?.captureException(error)
      return null
    } finally {
      commit(Mutations.SET_LOADING, false)
    }
  },

  /**
   * Fetch all user default views for a specific board
   * @param {Object} context - Vuex context
   * @param {String} boardId - The board ID to fetch all default views for
   * @returns {Promise} - All user default views data
   */
  async fetchAllDefaultViews({ commit }, boardId) {
    if (!boardId) return []

    commit(Mutations.SET_LOADING, true)

    try {
      const response = await getAllDefaultViews({ boardId })
      return response
    } catch (error) {
      logger.error('Error fetching all default views:', error.message)
      Sentry?.captureException(error)
      return []
    } finally {
      commit(Mutations.SET_LOADING, false)
    }
  },

  /**
   * Set a default view for a specific user
   * @param {Object} context - Vuex context
   * @param {Object} payload - Action payload
   * @param {String} payload.boardId - The board ID
   * @param {String} payload.viewId - The view ID to set as default
   * @param {String} payload.userId - The user ID to set the default view for
   * @returns {Promise} - The result of the operation
   */
  async setDefaultViewForUser({ commit }, { boardId, viewId, userId }) {
    if (!boardId || !viewId || !userId) return null

    commit(Mutations.SET_LOADING, true)

    try {
      const response = await setDefaultViewForUser({ boardId, viewId, userId })
      return response
    } catch (error) {
      logger.error('Error setting default view for user:', error.message)
      Sentry?.captureException(error)
      return null
    } finally {
      commit(Mutations.SET_LOADING, false)
    }
  },

  /**
   * Remove the default view for a specific user
   * @param {Object} context - Vuex context
   * @param {Object} payload - Action payload
   * @param {String} payload.boardId - The board ID
   * @param {String} payload.userId - The user ID to remove the default view for
   * @returns {Promise} - The result of the operation
   */
  async removeDefaultViewForUser({ commit }, { boardId, userId }) {
    if (!boardId || !userId) return null

    commit(Mutations.SET_LOADING, true)

    try {
      const response = await removeDefaultViewForUser({ boardId, userId })
      return response
    } catch (error) {
      logger.error('Error removing default view for user:', error.message)
      Sentry?.captureException(error)
      return null
    } finally {
      commit(Mutations.SET_LOADING, false)
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
  modules: {}
}
